import React, { useEffect, useState, useRef } from "react";
import Tree from "react-d3-tree";

const containerStyles = {
  width: "100%",
  height: "100%",
};

const legendItems = [
  { color: "#AB8EC1", label: "Project Goal" },
  { color: "#E4A4B6", label: "Primary Driver" },
  { color: "#5BC6CD", label: "Secondary Driver" },
  { color: "#00B5E0", label: "Change Idea" },
];

const Legend = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginTop: "20px",
        flexDirection: "column",
        width: "200px",
      }}
    >
      <span
        style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}
      >
        Driver Diagram Legend
      </span>
      {legendItems.map((item, index) => (
        <div
          key={index}
          style={{ display: "flex", alignItems: "center", marginRight: "20px" }}
        >
          <div
            style={{
              width: "15px",
              height: "15px",
              backgroundColor: item.color,
              marginRight: "5px",
              border: "1px solid white",
            }}
          ></div>
          <span style={{ fontSize: "12px", fontWeight: "semibold" }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export interface DriverDiagramProps {
  key: number;
  goal: string;
  primaryDrivers: {
    name: string;
    secondaryDrivers: { name: string; changeIdea: { name: string }[] }[];
  }[];
}

const DriverDiagram = ({ goal, primaryDrivers }: DriverDiagramProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setDimensions({ width, height });
      setTranslate({ x: width / 5, y: height / 2 });
    }
  }, []);

  const data = [
    {
      name: goal,
      children: primaryDrivers.map((primaryDriver) => ({
        name: primaryDriver.name,
        children: primaryDriver.secondaryDrivers.map((secondaryDriver) => ({
          name: secondaryDriver.name,
          children: secondaryDriver?.changeIdea?.map((noLinkNode) => ({
            name: noLinkNode.name,
          })),
        })),
      })),
    },
  ];

  const splitTextIntoLines = (text: string, maxLineLength: number) => {
    const words = text ? text.split(" ") : [""];
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + word).length <= maxLineLength) {
        currentLine += `${word} `;
      } else {
        lines.push(currentLine.trim());
        currentLine = `${word} `;
      }
    });

    if (currentLine) {
      lines.push(currentLine.trim());
    }

    return lines;
  };

  const renderCustomNodeElement = ({ nodeDatum, hierarchyPointNode }: any) => {
    let fillColor;
    switch (hierarchyPointNode.depth) {
      case 0:
        fillColor = "#AB8EC1";
        break;
      case 1:
        fillColor = "#E4A4B6";
        break;
      case 2:
        fillColor = "#5BC6CD";
        break;
      case 3:
        fillColor = "#00B5E0";
        break;
      default:
        fillColor = "lightgrey";
    }

    const lines = splitTextIntoLines(nodeDatum.name, 20);
    const rectHeight = Math.max(50, lines.length * 16 + 10);

    return (
      <g>
        <rect
          width="150"
          height={rectHeight}
          x="-75"
          y={-rectHeight / 1.7}
          fill={fillColor}
          stroke="white"
        />
        <text
          x="0"
          y={-rectHeight / 2 + 10}
          textAnchor="middle"
          fill="#ffffff"
          dominantBaseline="middle"
          fontSize={10}
          strokeWidth="0.4"
        >
          {lines.map((line, index) => (
            <tspan key={index} x="0" dy={index === 0 ? "1em" : "1.2em"}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  const downloadDiagram = () => {
    if (svgRef.current) {
      const svgData = new XMLSerializer().serializeToString(svgRef.current);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);

      const downloadLink = document.createElement("a");
      downloadLink.href = svgUrl;
      downloadLink.download = "driver_diagram.svg";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  return (
    <div ref={containerRef} style={containerStyles}>
      {dimensions.width > 0 && dimensions.height > 0 && (
        <Tree
          data={data}
          dimensions={dimensions}
          translate={translate}
          collapsible={true}
          pathFunc={"step"}
          renderCustomNodeElement={renderCustomNodeElement}
          orientation="horizontal"
          nodeSize={{ x: 200, y: 100 }}
          // svgRef={svgRef} // Assign the ref
        />
      )}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          backgroundColor: "#f1f1f1",
          border: "1px solid #ccc",
          padding: "10px",
        }}
      >
        <Legend />
        <button onClick={downloadDiagram} style={{ marginTop: "10px" }}>
          Download Diagram
        </button>
      </div>
    </div>
  );
};

export default DriverDiagram;
